import type { ComponentChild, FunctionComponent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import type { TemplateContextMap, TemplateFunction } from 'Components/types';
// import { vehicleColorUpdated } from 'Core/actions/fitmentSearch';
import { selectedVehicleColorIdSelector } from 'Core/reducers/fitmentSearch/storage';
import { searchResponseExtraSelector } from 'Core/selectors/search';
import { useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import isTruthy from 'Utils/isTruthy';

const configDefaults: AutoSyncConfig = {
  height: '110px',
  vehicleImageSize: 640,
  vehicleAngles: [32],
  showColorPicker: false,
};
const vehicleVisualizedClass = 'cm_AutoSync-visualized';

export interface Props {
  template: TemplateFunction<Params>;
  toggleGridButtonVisibility?: boolean;
  config?: AutoSyncConfig;
  wheelMpn?: string;
  extraParams?: TemplateContextMap;
}

export type Params = {
  visualization: ComponentChild;
  isVehicleError: boolean;
  isWheelError: boolean;
} & TemplateContextMap;

export interface AutoSyncVehicleOptions {
  colorId?: number;
}

// docs: https://vvs.autosyncstudio.com/express/docs/reference/classes/VvsExpress.VvsExpress.html
export type InitVvsExpressProps = AutoSyncConfig & {
  apiUrl: string;
  apiKey: string;
};

export interface AutoSyncConfig {
  el?: HTMLElement | null;
  height?: string;
  vehicleImageSize?: number;
  vehicleAngles?: number[];
  showColorPicker?: boolean;
  customCss?: string;
  widthAdjustment?: string;
}

interface Color {
  Code: string;
  Id: number;
  Img001: boolean;
  Img014: boolean;
  Img032: boolean;
  Name: string;
  Rgb1: string;
  Rgb2: string;
  ShortName: string;
  Swatch: string;
}

export interface AutoSyncVisualizer {
  loadVehicleById: (vehicleId: number, options?: AutoSyncVehicleOptions) => Promise<void>;
  loadWheelByPn: (wheelMpn: string) => Promise<void>;
  setHeight: (height: string) => void;
  setVehicleColor: (colorId: number) => void;
  onVehicleColorChange: (callback: (color: Color) => void) => void;
}

const AutoSyncVisualization: FunctionComponent<Props> = ({
  template,
  toggleGridButtonVisibility = true,
  config,
  wheelMpn,
  extraParams,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const visualizerRef = useRef<AutoSyncVisualizer>();
  const visualizerConfigRef = useRef<AutoSyncConfig>({ ...configDefaults, ...(config ?? {}) });
  const toggleGridButtonVisibilityRef = useRef(toggleGridButtonVisibility);

  // const dispatch = useDispatch();

  const [isVehicleError, setIsVehicleError] = useState(false);
  const [isWheelError, setIsWheelError] = useState(false);
  const [isVisualizationHidden, setIsVisualizationHidden] = useState(false);

  const vehicleIdRaw = useSelector(searchResponseExtraSelector)?.AutoSyncVehicleId;
  // The wire format historically permits both `string` and `number` ids;
  const vehicleId = vehicleIdRaw == null ? undefined : String(vehicleIdRaw);
  const colorId = useSelector(selectedVehicleColorIdSelector);

  const visualizeVehicle = useCallback(() => {
    if (!vehicleId) {
      return;
    }

    if (!visualizerRef.current) {
      const candidate = window.initVvsExpress?.({
        apiUrl: 'https://api.autosyncstudio.com/',
        apiKey: fitmentSearchConfig.autoSyncKey ?? '',
        el: containerRef.current,
        ...visualizerConfigRef.current,
      });
      // The third-party `initVvsExpress` is not under our control;
      // if it ever ships a build whose return value is missing one of the methods
      // we call below, bail out instead of crashing on an undefined invocation later.
      visualizerRef.current = isAutoSyncVisualizer(candidate) ? candidate : undefined;
    }

    if (!visualizerRef.current) {
      return;
    }

    if (toggleGridButtonVisibilityRef) {
      window.document.body.classList.remove(vehicleVisualizedClass);
    }

    const options: AutoSyncVehicleOptions = {};
    if (colorId) {
      options.colorId = +colorId;
    }

    tryVisualizeVehicle(visualizerRef, options, Number(vehicleId))
      .then(() => {
        setIsVehicleError(false);
        setIsVisualizationHidden(false);

        // IMPORTANT: temporary Color Update feature is disabled, because something is wrong with the AutoSync API
        // visualizerRef.current?.onVehicleColorChange((color: Color) => {
        //   if (color.Name && color.Id) {
        //     dispatch(
        //       vehicleColorUpdated({
        //         vehicle: selectedVehicle,
        //         vehicleExtra: {
        //           colorId: color.Id.toString(),
        //         },
        //       }),
        //     );
        //   }
        // });

        if (toggleGridButtonVisibilityRef) {
          window.document.body.classList.add(vehicleVisualizedClass);
        }
      })
      .catch((err: Error) => {
        setIsVehicleError(true);
        setIsVisualizationHidden(true);
        console.warn(err.message);
      });

    if (wheelMpn) {
      visualizerRef.current
        .loadWheelByPn(wheelMpn)
        .then(() => {
          setIsWheelError(false);
        })
        .catch((err: Error) => {
          setIsWheelError(true);
          console.warn(`Failed to visualize the '${wheelMpn}' wheel. ${err.message}`);
        });
    }
  }, [wheelMpn, colorId, vehicleId]);

  useEffect(() => {
    try {
      visualizeVehicle();
    } catch (error) {
      console.error('Failed to visualize a vehicle', error);
    }
  }, [visualizeVehicle]);

  useEffect(() => {
    const height = isVisualizationHidden ? '0px' : (visualizerConfigRef.current?.height ?? '110px');
    try {
      visualizerRef.current?.setHeight(height);
    } catch (error) {
      console.error('Failed to set height', error);
    }
  }, [isVisualizationHidden]);

  if (!vehicleId) {
    return null;
  }

  const visualization = (
    <div
      ref={containerRef}
      className={['cm_vehicle-visualization', isVehicleError && 'cm_hide'].filter(isTruthy).join(' ')}
    ></div>
  );

  return template.call({ visualization, isVehicleError, isWheelError, ...extraParams });
};

function isAutoSyncVisualizer(value: unknown): value is AutoSyncVisualizer {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v.loadVehicleById === 'function' &&
    typeof v.loadWheelByPn === 'function' &&
    typeof v.setHeight === 'function'
  );
}

async function tryVisualizeVehicle(
  visualizerRef: { current: AutoSyncVisualizer | undefined },
  options: AutoSyncVehicleOptions,
  vehicleId: number,
) {
  try {
    await visualizerRef.current?.loadVehicleById(vehicleId, options);
  } catch {
    console.warn(`Failed to visualize the vehicle. Vehicle ID: ${vehicleId}`);
  }
}

export default AutoSyncVisualization;
